import {
  type SelectItem,
  type SelectProps,
  useSelectQueryGate,
} from '@vrplatform/components';
import { Logo } from '@vrplatform/components-legacy';
import {
  type api,
  getRecentTeamIdsStorageKey,
  parseStoredRecentTeamIds,
  useApiQuery,
  useDashboard,
  useInfiniteApiQuery,
  useMe,
  useTeam,
  useTeamIdContext,
  useTeamQuery as useRichTeamQuery,
  useTeamRole,
  useTeamSwitch,
} from '@vrplatform/data';
import { Icon, PlusIcon } from '@vrplatform/icons';
import {
  parseAsBoolean,
  parseAsString,
  useQueryStates,
} from '@vrplatform/query-params';
import {
  DEMO_TEST_PARTNER_TENANT_ID,
  VRP_TENANT_ID,
} from '@vrplatform/ui-common';
import { hasValue, isUUID } from '@vrplatform/utils';
import { Badge, Box, Center, Indicator, Menu, Tooltip } from '@mantine/core';
import { type JSX, useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  buildTeamSwitchVisibleData,
  MIN_TEAM_SWITCH_GROUPED_TEAM_COUNT,
  resolveTeamSwitchValue,
} from './TeamSwitch.utils';
import {
  TeamSwitchSelect,
  type TeamSwitchTriggerProps,
} from './TeamSwitchSelect';
import {
  getTeamAsyncOperation,
  isTeamAsyncOperationActive,
} from '../../menus/team/TeamAsyncOperationStatus.utils';

type ApiTeam =
  api['/me/teams']['get']['responses']['200']['content']['application/json']['data'][number];

type TeamSwitchVisibility = 'always' | 'when-switchable';

type NavbarTeamSwitchProps = {
  variant?: 'navbar';
  isNavigationExpanded?: boolean;
};

type MenuSectionTeamSwitchProps = {
  variant: 'menu-section';
};

type CustomTeamSwitchProps = {
  variant: 'custom';
  dropdownProps?: SelectProps<string>['dropdownProps'];
  header?: string | null;
  isNavigationExpanded?: boolean;
  renderTrigger: (props: TeamSwitchTriggerProps) => JSX.Element;
  tone?: 'navbar' | 'default';
  visibility?: TeamSwitchVisibility;
};

export type TeamSwitchProps =
  | NavbarTeamSwitchProps
  | MenuSectionTeamSwitchProps
  | CustomTeamSwitchProps;

const TEAM_SWITCH_PAGE_LIMIT = 50;
const TEAM_SWITCH_CACHE_STALE_TIME = 5 * 60 * 1000;

function useTeamQuery(
  search: string,
  {
    currentTeamId,
    recentTeamIds = [],
    shouldFetchOptions = true,
  }: {
    currentTeamId?: string | null;
    recentTeamIds?: string[];
    shouldFetchOptions?: boolean;
  } = {}
) {
  const { id: userId } = useMe();
  const { isVrpAdmin } = useTeamRole();

  const getTeamSelectItem = useCallback((team: ApiTeam): SelectItem => {
    const isPartnerTeam = team.type === 'partner';
    // Partner teams are created in the VRTrust storage realm, so only
    // property managers get the VRTrust marker.
    const isGeneralLedger = !!team.isGeneralLedger && !isPartnerTeam;
    const isInactive = team.status === 'inactive';
    const isDirectBillingPartner = [
      VRP_TENANT_ID,
      DEMO_TEST_PARTNER_TENANT_ID,
    ].includes(team.billingPartnerId ?? '');
    const isDeleting = isTeamAsyncOperationActive(
      getTeamAsyncOperation(team, 'team-delete')
    );

    return {
      value: team.id,
      label: team.name || '-',
      disabled: isDeleting ? 'Team deletion is in progress' : undefined,
      rightSection: isDeleting ? (
        <Badge color="blue" size="xs">
          Deleting...
        </Badge>
      ) : undefined,
      icon: isGeneralLedger ? (
        <Center
          sx={{
            borderRadius: '100%',
          }}
        >
          <Tooltip label="VRTrust">
            <Indicator disabled={!isInactive} color="orange" size={6}>
              <Box>
                <Logo width={14} />
              </Box>
            </Indicator>
          </Tooltip>
        </Center>
      ) : isInactive ? (
        <Tooltip label={isDirectBillingPartner ? 'Cancelled' : 'Suspended'}>
          <Icon icon="Lock2Icon" size={14} />
        </Tooltip>
      ) : isPartnerTeam ? (
        <Tooltip label="Partner team">
          <Icon icon="UsersIcon" size={14} />
        </Tooltip>
      ) : undefined,
    };
  }, []);

  const queryData = useInfiniteApiQuery(
    async (api, signal, args, page: number) => {
      const searchIsTeamId = isUUID(args.search);

      const result = await api.GET('/me/teams', {
        params: {
          query: {
            limit: TEAM_SWITCH_PAGE_LIMIT,
            page,
            ids: searchIsTeamId ? args.search : undefined,
            search: args.search && !searchIsTeamId ? args.search : undefined,
            sort: 'type',
          },
        },
        signal,
      });

      if (result.error) throw result.error;

      return {
        list: result.data.data,
        aggregate: result.data.pagination.total,
        pagination: result.data.pagination,
      };
    },
    {
      queryGroup: 'teams',
      uniqueQueryKey: 'team-switch-options',
      skip: !userId || !shouldFetchOptions,
      staleTime: TEAM_SWITCH_CACHE_STALE_TIME,
      teamScoped: false,
      initialPageParam: 1,
      getNextPageParam: (lastPage) => lastPage.pagination.nextPage ?? null,
      variables: {
        userId,
        search: search.trim(),
      },
    }
  );

  const lookupTeamIds = useMemo(
    () => [
      ...new Set(
        [...recentTeamIds, currentTeamId].filter(
          (teamId): teamId is string => !!teamId
        )
      ),
    ],
    [currentTeamId, recentTeamIds]
  );

  const { data: lookupTeams, isLoading: isLoadingLookup } = useApiQuery(
    async (api, signal, args) => {
      const result = await api.GET('/me/teams', {
        params: {
          query: {
            ids: args.lookupTeamIds.join(','),
            limit: args.lookupTeamIds.length,
          },
        },
        signal,
      });

      if (result.error) throw result.error;

      return result.data.data;
    },
    {
      queryGroup: 'teams',
      uniqueQueryKey: 'team-switch-lookup',
      keepPreviousData: true,
      skip: !userId || lookupTeamIds.length === 0,
      staleTime: TEAM_SWITCH_CACHE_STALE_TIME,
      teamScoped: false,
      variables: {
        userId,
        lookupTeamIds,
      },
    }
  );

  const { data: accessData, isLoading: isLoadingAccess } = useApiQuery(
    async (api, signal) => {
      const result = await api.GET('/me/teams', {
        params: { query: { limit: 1 } },
        signal,
      });

      if (result.error) throw result.error;

      // The VRPlatform admin team is exposed as a custom action, not a row.
      return result.data.pagination.total;
    },
    {
      queryGroup: 'teams',
      uniqueQueryKey: 'team-switch-access',
      // VRPlatform admins can access every team, so the count is not needed.
      skip: !userId || isVrpAdmin,
      staleTime: TEAM_SWITCH_CACHE_STALE_TIME,
      teamScoped: false,
      variables: {
        userId,
      },
    }
  );

  const mappedQueryData = useMemo(() => {
    if (!queryData.data) return queryData;

    return {
      ...queryData,
      data: {
        ...queryData.data,
        pages: queryData.data.pages.map((page) => ({
          ...page,
          list: page.list.map(getTeamSelectItem),
        })),
      },
    };
  }, [getTeamSelectItem, queryData]);

  const lookupTeamsById = useMemo(
    () => new Map((lookupTeams ?? []).map((team) => [team.id, team] as const)),
    [lookupTeams]
  );
  const recentTeams = useMemo(
    () =>
      recentTeamIds
        .map((teamId) => lookupTeamsById.get(teamId))
        .filter((team): team is ApiTeam => !!team)
        .map(getTeamSelectItem),
    [getTeamSelectItem, lookupTeamsById, recentTeamIds]
  );
  const currentApiTeam = currentTeamId
    ? lookupTeamsById.get(currentTeamId)
    : undefined;
  const currentTeam = currentApiTeam ? getTeamSelectItem(currentApiTeam) : null;
  const shouldGroupTeams =
    isVrpAdmin || (accessData ?? 0) >= MIN_TEAM_SWITCH_GROUPED_TEAM_COUNT;

  const visibleData = useMemo(
    () =>
      buildTeamSwitchVisibleData({
        pages: mappedQueryData.data?.pages || [],
        recentTeamIds,
        recentTeams,
        shouldGroupTeams,
      }),
    [mappedQueryData.data?.pages, recentTeamIds, recentTeams, shouldGroupTeams]
  );

  const filteredQueryData = useMemo(() => {
    if (!mappedQueryData.data) return mappedQueryData;

    return {
      ...mappedQueryData,
      data: {
        ...mappedQueryData.data,
        pages: visibleData.pages,
      },
    };
  }, [mappedQueryData, visibleData.pages]);

  return {
    queryData: filteredQueryData,
    loadingValue:
      isLoadingAccess || (lookupTeamIds.length > 0 && isLoadingLookup),
    canSwitch: isVrpAdmin || (accessData ?? 0) > 1,
    currentTeam,
    pinnedItems: visibleData.pinnedItems,
  };
}

export const TeamSwitch = (props: TeamSwitchProps) => {
  const variant = props.variant ?? 'navbar';
  const [searchParams] = useSearchParams();
  const [, setDrawer] = useQueryStates({
    'add-team-drawer': parseAsBoolean,
    'add-team-name': parseAsString,
  });

  const [team] = useTeam();
  const { teamId: selectedTeamId } = useTeamIdContext();
  const { id: meId } = useMe();
  const { isPartnerAdmin, isVrpAdmin } = useTeamRole();
  // the partner name only labels the switcher for VRP admins; do not fetch
  // the rich team on normal startup
  const { data: richTeamData } = useRichTeamQuery({ skip: !isVrpAdmin });
  const setTeamId = useTeamSwitch();
  const goto = useNavigate();
  const [dashboard, toggleTeamOverview] = useDashboard();
  const activeTeamId = selectedTeamId ?? team.id;

  const [search, setSearch] = useState('');
  const recentTeamIds = useMemo(() => {
    if (!meId) return [];

    return parseStoredRecentTeamIds(
      globalThis.localStorage?.getItem(getRecentTeamIdsStorageKey(meId)) ?? null
    ).filter((teamId) => teamId !== VRP_TENANT_ID);
    // Re-read storage after useTeamSwitch records the newly selected team.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meId, team.id]);

  const gate = useSelectQueryGate({ value: activeTeamId });
  const { queryData, loadingValue, canSwitch, currentTeam, pinnedItems } =
    useTeamQuery(search, {
      currentTeamId: activeTeamId,
      recentTeamIds,
      shouldFetchOptions: gate.shouldFetchOptions,
    });

  const value = useMemo(
    () =>
      resolveTeamSwitchValue({
        dashboard,
        currentTeam,
        hydratedTeamId: team.id,
        hydratedTeamName: team.name,
        selectedTeamId,
      }),
    [dashboard, currentTeam, team.id, team.name, selectedTeamId]
  );

  const setNewTeamValue = useCallback(
    (newValue: SelectItem | null) => {
      if (!newValue?.value) return;
      void setTeamId(newValue.value);

      const hasKeys = [...searchParams.values()].filter(hasValue).length > 0;
      if (hasKeys) {
        goto(window.location.pathname);
      }
      toggleTeamOverview(false);
    },
    [goto, searchParams, setTeamId, toggleTeamOverview]
  );

  const customActionTop = useMemo(
    () => ({
      label: 'VRPlatform',
      icon: (
        <Center
          sx={{
            borderRadius: '100%',
            overflow: 'hidden',
          }}
        >
          <Logo width={16} />
        </Center>
      ),
      onSubmit: () =>
        setNewTeamValue({
          label: 'VRPlatform',
          value: VRP_TENANT_ID,
        }),
    }),
    [setNewTeamValue]
  );

  const visibility =
    props.variant === 'menu-section'
      ? 'when-switchable'
      : props.variant === 'custom'
        ? (props.visibility ?? 'always')
        : 'always';

  if (visibility === 'when-switchable' && !canSwitch) return null;

  const isNavigationExpanded =
    props.variant === 'menu-section'
      ? false
      : (props.isNavigationExpanded ?? true);
  const header =
    props.variant === 'menu-section'
      ? null
      : props.variant === 'custom' && props.header !== undefined
        ? props.header
        : isVrpAdmin
          ? richTeamData?.team.partnerName || 'Team'
          : 'Team';
  const tone =
    props.variant === 'custom'
      ? (props.tone ?? 'navbar')
      : props.variant === 'menu-section'
        ? 'default'
        : 'navbar';
  const renderTrigger =
    props.variant === 'menu-section'
      ? () => (
          <Menu.Item
            leftSection={
              <Icon
                icon="OfficeIcon"
                size={16}
                color={(theme) => theme.colors.gray[6]}
              />
            }
          >
            Select team
          </Menu.Item>
        )
      : props.variant === 'custom'
        ? props.renderTrigger
        : undefined;
  const dropdownProps =
    props.variant === 'menu-section'
      ? ({
          withinPortal: false,
          position: 'bottom-end',
          width: 260,
        } satisfies SelectProps<string>['dropdownProps'])
      : props.variant === 'custom'
        ? props.dropdownProps
        : undefined;

  return (
    <>
      <TeamSwitchSelect
        value={value}
        canSwitch={canSwitch}
        containerPaddingTop={variant === 'menu-section' ? 0 : 5}
        pinnedItems={pinnedItems}
        customActionTop={isVrpAdmin ? customActionTop : undefined}
        customBottomActions={
          variant !== 'menu-section' && (isPartnerAdmin || isVrpAdmin)
            ? [
                {
                  label: 'Add team',
                  icon: <PlusIcon />,
                  onSubmit: (teamName) => {
                    setDrawer({
                      'add-team-drawer': true,
                      'add-team-name': teamName,
                    });
                  },
                  id: 'add-team',
                },
              ]
            : undefined
        }
        dropdownProps={dropdownProps}
        loadingQuery={loadingValue}
        onOpenChange={gate.onOpenChange}
        queryData={queryData}
        header={header}
        isNavigationExpanded={isNavigationExpanded}
        renderTrigger={renderTrigger}
        tone={tone}
        setSearch={setSearch}
        setValue={setNewTeamValue}
      />
      {variant === 'menu-section' && <Menu.Divider />}
    </>
  );
};
